import React from 'react'
import { useQuery } from 'react-query'
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Text,
} from '@chakra-ui/react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'

interface LeaderboardEntry {
  user_id: string
  points: number
}

interface LeaderboardResponse {
  leaderboard_entries: LeaderboardEntry[]
  your_user_id?: string
  your_points?: number
  your_rank?: number
}

export default function Leaderboard() {
  const { isAuthenticated, getAccessToken } = useAuth()

  const { data: leaderboard, isLoading, error } = useQuery<LeaderboardResponse>(
    'leaderboard',
    async () => {
      const token = await getAccessToken()
      const response = await api.get('/get_leaderboard', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    },
    {
      enabled: isAuthenticated,
    }
  )

  if (!isAuthenticated) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="info">
          <AlertIcon />
          Please log in to view the leaderboard.
        </Alert>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container maxW="container.md" py={8}>
        <Box display="flex" justifyContent="center" py={8}>
          <Spinner size="xl" />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error">
          <AlertIcon />
          Failed to load leaderboard. Please try again.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Heading mb={6}>Leaderboard</Heading>
      
      {/* User's position */}
      {leaderboard?.your_rank && (
        <Box mb={6} p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
          <Text fontSize="lg" fontWeight="bold" color="blue.800">
            Your Position: #{leaderboard.your_rank} with {leaderboard.your_points} points
          </Text>
        </Box>
      )}
      
      <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Rank</Th>
              <Th>User</Th>
              <Th isNumeric>Points</Th>
            </Tr>
          </Thead>
          <Tbody>
            {leaderboard?.leaderboard_entries?.map((entry, index) => (
              <Tr key={entry.user_id}>
                <Td>
                  <Badge
                    colorScheme={index < 3 ? 'yellow' : 'gray'}
                    variant={index < 3 ? 'solid' : 'outline'}
                  >
                    #{index + 1}
                  </Badge>
                </Td>
                <Td fontWeight="medium">{entry.user_id}</Td>
                <Td isNumeric fontWeight="bold">
                  {entry.points}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      
      {(!leaderboard?.leaderboard_entries || leaderboard.leaderboard_entries.length === 0) && (
        <Box mt={4} p={4} bg="gray.50" borderRadius="md" textAlign="center">
          <Text color="gray.600">No leaderboard entries yet. Start solving problems to appear on the leaderboard!</Text>
        </Box>
      )}
    </Container>
  )
} 